import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useFormikContext } from "formik";
import classNames from "classnames/bind";
import { Input, Space, Card } from "@masterteam/mt-design-react";

import styles from "./FormulaBuilder.module.scss";

const cx = classNames.bind(styles);

const operations = ["-", "+", "*", "/"];

function FormulaBuilder({ isEditMode, isReadOnly }) {
  const intl = useIntl();
  const { values, setFieldValue, errors } = useFormikContext();
  const [showOptions, setShowOptions] = useState(false);
  const inputRef = useRef();

  const handleClickOutside = (event) => {
    if (!inputRef?.current?.contains(event.target)) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formula = values?.Formula || [];
  const parameters =
    values?.Parameters?.map((item) => item.ParameterName)?.filter(
      (item) => item !== ""
    ) || [];
  const error = errors?.Formula || null;

  const handleKeyDown = (evt) => {
    evt.preventDefault();

    if ((evt.keyCode === 8 || evt.keyCode === 46) && formula?.length) {
      const newFormula = formula.slice(0, -1);
      setFieldValue("Formula", newFormula);
    }
  };

  const handleClick = (option) => {
    const newFormula = [...formula, option];
    setFieldValue("Formula", newFormula);
  };

  const showingOptions = [];

  const lastChar = formula?.[formula?.length - 1];
  const openCount = (formula?.filter((item) => item === "(") || []).length;
  const closeCount = (formula?.filter((item) => item === ")") || []).length;

  if (
    lastChar !== ")" &&
    !parameters.includes(lastChar) &&
    lastChar !== "100%"
  ) {
    showingOptions.push(...parameters);
  }

  if ((operations.includes(lastChar) || !lastChar) && lastChar !== "100%") {
    showingOptions.push("(");
  }

  if (
    !operations.includes(lastChar) &&
    lastChar !== "(" &&
    openCount > closeCount &&
    lastChar !== "100%"
  ) {
    showingOptions.push(")");
  }

  if (
    lastChar &&
    !operations.includes(lastChar) &&
    lastChar !== "(" &&
    lastChar !== "100%"
  ) {
    showingOptions.push(...operations);
  }

  if (lastChar === "*" && values?.UnitOfMeasure?.value === "Percentage") {
    showingOptions.push("100%");
  }

  return (
    <div className={styles.formulaBuilder}>
      <Space direction="vertical" align="start">
        <span className={styles.title}>
          {intl.formatMessage({ id: "kpi.parameter-formula" })}
        </span>
        <div ref={inputRef} className={styles.inputContainer}>
          <Input
            type="text"
            placeholder={`X / Y${
              values?.UnitOfMeasure?.value === "Percentage" ? " * 100%" : ""
            }`}
            title={error && intl.formatMessage({ id: "error.invalid-formula" })}
            error={error}
            onKeyDown={handleKeyDown}
            onChange={() => {}}
            value={formula.join("")}
            onFocus={() => setShowOptions(true)}
            disabled={isEditMode || isReadOnly}
            // readOnly
          />
          <Card
            className={cx({
              options: true,
              active: showOptions && showingOptions?.length,
            })}
          >
            {showingOptions.map((option) => (
              <div
                key={option}
                onClick={() => handleClick(option)}
                className={styles.option}
              >
                <strong>{option}</strong>
              </div>
            ))}
          </Card>
        </div>
      </Space>
    </div>
  );
}

export default FormulaBuilder;
